import { useEffect, useRef, useState } from 'react';
import useModal from '../../../hooks/use-close-modal.hook';
import { ReportsService, type CallDetail } from '../../../services/reports.service';
import { AlertService } from '../../../services/alert.service';
import Icon from '../../../components/forms/icon.component';
import { CPD_COLORS } from '../dashboard/dashboard.charts';
import { fmtDateTime, fmtSecs } from './reports.types';

const EVENT_META: Record<string, { label: string; icon: string; color: string }> = {
  channel_create: { label: 'Originada', icon: 'ki-phone', color: 'primary' },
  channel_answer: { label: 'Contestada (200 OK)', icon: 'ki-check', color: 'info' },
  cpd_result: { label: 'Veredicto CPD', icon: 'ki-voice', color: 'dark' },
  channel_hangup: { label: 'Colgada', icon: 'ki-cross-circle', color: 'warning' }
};

/** GET /v1/reports/calls/:callUuid → timeline de eventos de una llamada (ClickHouse). */
export default function CallDetailModal({
  callUuid,
  show,
  onRequestClose
}: {
  callUuid: string;
  show: boolean;
  onRequestClose: () => void;
}) {
  const modalRef = useRef<HTMLDivElement>(null);
  useModal(modalRef, show);
  const [detail, setDetail] = useState<CallDetail | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!show) return;
    let alive = true;
    setLoading(true);
    ReportsService.getCallDetail(callUuid)
      .then((d) => alive && setDetail(d))
      .catch((e: any) => AlertService.toastError(e?.error || e || 'No se encontró la llamada'))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [callUuid, show]);

  const close = () => {
    document.querySelectorAll('.modal-backdrop').forEach((el) => el.remove());
    document.body.classList.remove('modal-open');
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
    onRequestClose();
  };

  const t0 = detail?.events?.[0] ? new Date(detail.events[0].ts).getTime() : 0;

  return (
    <div
      className="modal fade show d-block"
      ref={modalRef}
      style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
      data-bs-backdrop="static"
    >
      <div className="modal-dialog modal-dialog-centered mw-800px">
        <div className="modal-content shadow-lg border-0">
          <div className="modal-header justify-content-end border-0 pb-0 pt-4 px-6">
            <button
              type="button"
              className="btn btn-sm btn-icon btn-light-danger rounded-circle"
              onClick={close}
            >
              <i className="ki-duotone ki-cross fs-2">
                <span className="path1" />
                <span className="path2" />
              </i>
            </button>
          </div>
          <div className="modal-body pt-0 pb-10 px-6 px-xl-10">
            <div className="text-center mb-6">
              <h1 className="mb-2 fs-2x">Detalle de llamada</h1>
              <code className="fs-7 text-muted">{callUuid}</code>
              <div className="separator border-2 my-4"></div>
            </div>

            {loading && <div className="text-center text-muted py-10">Cargando…</div>}

            {detail && (
              <>
                <div className="row g-3 mb-6 fs-7">
                  <div className="col-md-3">
                    <div className="text-muted fs-8">Operador</div>
                    <div className="fw-bold text-uppercase">{detail.operatorId || '—'}</div>
                  </div>
                  <div className="col-md-3">
                    <div className="text-muted fs-8">Grupo de ruta</div>
                    <div className="fw-bold">{detail.routeGroupId ?? '—'}</div>
                  </div>
                  <div className="col-md-3">
                    <div className="text-muted fs-8">A-number</div>
                    <div className="fw-bold font-monospace">{detail.aNumber || '—'}</div>
                  </div>
                  <div className="col-md-3">
                    <div className="text-muted fs-8">B-number</div>
                    <div className="fw-bold font-monospace">{detail.bNumber || '—'}</div>
                  </div>
                </div>

                <div className="timeline timeline-border-dashed">
                  {detail.events.map((ev, i) => {
                    const m = EVENT_META[ev.eventType] ?? {
                      label: ev.eventType,
                      icon: 'ki-information',
                      color: 'secondary'
                    };
                    const dt = t0 ? ((new Date(ev.ts).getTime() - t0) / 1000).toFixed(1) : '0.0';
                    return (
                      <div className="timeline-item" key={`${ev.ts}-${i}`}>
                        <div className="timeline-line w-40px"></div>
                        <div className="timeline-icon symbol symbol-circle symbol-40px">
                          <div className={`symbol-label bg-light-${m.color}`}>
                            <Icon icon={m.icon} size={`fs-3 text-${m.color}`} />
                          </div>
                        </div>
                        <div className="timeline-content mb-8 mt-n1">
                          <div className="d-flex align-items-center gap-3 mb-1">
                            <span className="fw-bold fs-6">{m.label}</span>
                            <span className="badge badge-light fs-9">+{dt} s</span>
                            <span className="text-muted fs-8">{fmtDateTime(ev.ts)}</span>
                            <span className="text-muted fs-8">· {ev.fsNode}</span>
                          </div>
                          <div className="fs-7 text-gray-700 d-flex flex-wrap gap-3">
                            {ev.eventType === 'cpd_result' && (
                              <>
                                <span>
                                  veredicto:{' '}
                                  <b
                                    style={{
                                      color:
                                        (CPD_COLORS as any)[ev.cpdVerdict] ?? CPD_COLORS.unknown
                                    }}
                                  >
                                    {ev.cpdVerdict || '—'}
                                  </b>
                                </span>
                                <span>
                                  fuente: <code>{ev.cpdSource || '—'}</code>
                                </span>
                                <span>
                                  tag: <code>{ev.cpdOperatorTag || '—'}</code>
                                </span>
                                <span>detect: {ev.cpdDetectMs} ms</span>
                                {ev.bypassed && (
                                  <span className="badge badge-light-primary">bypass</span>
                                )}
                              </>
                            )}
                            {ev.eventType === 'channel_hangup' && (
                              <>
                                <span>
                                  cause: <code>{ev.hangupCause || '—'}</code>
                                </span>
                                <span>
                                  SIP: <b>{ev.sipTermStatus || '—'}</b>
                                </span>
                                <span>billsec: {fmtSecs(ev.billsec)}</span>
                                <span>duración: {fmtSecs(ev.duration)}</span>
                              </>
                            )}
                            {ev.eventType === 'channel_create' && (
                              <span>dirección: {ev.direction || '—'}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
